import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Send, Bot, User, Loader2 } from 'lucide-react';
import { api as axios } from '../lib/api';
import { isAxiosError } from 'axios';
import { useI18n } from '../i18n/i18n';
import ConversationSelector from './ConversationSelector';

interface ChatMessage {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
  sources?: Array<{
    type: string;
    title?: string;
    url?: string;
    snippet?: string;
  }>;
}

interface ChatResponse {
  answer: string;
  sources: Array<{
    type: string;
    title?: string;
    url?: string;
    snippet?: string;
  }>;
  metadata: {
    provider: string;
    model?: string;
    processingTime: number;
    timestamp: string;
    conversationId?: string;
  };
}

const ChatWidget: React.FC = () => {
  const { t, lang } = useI18n();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      const newHeight = Math.min(Math.max(inputRef.current.scrollHeight, 40), 120);
      inputRef.current.style.height = `${newHeight}px`;
    }
  }, [inputValue]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('chat_messages');
      if (saved) {
        const parsed = JSON.parse(saved) as ChatMessage[];
        const revived = parsed.map(m => ({ ...m, timestamp: new Date(m.timestamp) }));
        if (Array.isArray(revived) && revived.length > 0) {
          setMessages(revived);
          return;
        }
      }
    } catch {}
    const welcomeMessage: ChatMessage = {
      id: 'welcome',
      type: 'bot',
      content: t('chat.welcome'),
      timestamp: new Date(),
      sources: []
    };
    setMessages([welcomeMessage]);
  }, [t]);

  const loadConversationHistory = async (convSessionId: string) => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`/api/conversations/${convSessionId}/messages`);
      const historyMessages = response.data.map((msg: any) => ({
        id: msg.id,
        type: msg.role === 'user' ? 'user' : 'bot',
        content: msg.message,
        timestamp: new Date(msg.created_at)
      }));
      setMessages(historyMessages);
    } catch (error) {
      console.error('Failed to load conversation:', error);
      setError(t('chat.error.loadHistory'));
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSelectConversation = (convSessionId: string | null) => {
    setSessionId(convSessionId);
    setError(null);

    if (convSessionId) {
      loadConversationHistory(convSessionId);
    } else {
      const welcomeMessage: ChatMessage = {
        id: 'welcome',
        type: 'bot',
        content: t('chat.welcome'),
        timestamp: new Date(),
        sources: []
      };
      setMessages([welcomeMessage]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || loading) return;

    const userMessage: ChatMessage = {
      id: `user_${Date.now()}`,
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setLoading(true);
    setError(null);

    const progressMessageId = `progress_${Date.now()}`;
    const progressMessage: ChatMessage = {
      id: progressMessageId,
      type: 'bot',
      content: t('chat.thinking'),
      timestamp: new Date()
    };
    setMessages(prev => [...prev, progressMessage]);

    const progressTimers: number[] = [];
    const updateProgressMessage = (content: string) => {
      setMessages(prev =>
        prev.map(msg =>
          msg.id === progressMessageId ? { ...msg, content } : msg
        )
      );
    };

    progressTimers.push(window.setTimeout(() => updateProgressMessage(t('chat.progress.searching')), 15000));
    progressTimers.push(window.setTimeout(() => updateProgressMessage(t('chat.progress.processing')), 50000));
    progressTimers.push(window.setTimeout(() => updateProgressMessage(t('chat.progress.analyzing')), 80000));
    progressTimers.push(window.setTimeout(() => updateProgressMessage(t('chat.progress.finishing')), 120000));

    try {
      const payload: any = {
        question: userMessage.content,
        language: lang
      };

      if (sessionId) {
        payload.conversationId = sessionId;
      }

      const response = await axios.post<ChatResponse>('/api/chat', payload, { timeout: 300000 });
      progressTimers.forEach(timer => window.clearTimeout(timer));

      if (!sessionId && response.data.metadata.conversationId) {
        setSessionId(response.data.metadata.conversationId);
      }

      setMessages(prev =>
        prev.map(msg =>
          msg.id === progressMessageId
            ? {
                ...msg,
                content: response.data.answer,
                sources: response.data.sources
              }
            : msg
        )
      );
    } catch (err) {
      console.error('Chat error:', err);
      progressTimers.forEach(timer => window.clearTimeout(timer));
      const errorMessage = isAxiosError(err)
        ? err.response?.data?.error || t('chat.error.send')
        : t('chat.error.unexpected');

      setError(errorMessage);
      setMessages(prev =>
        prev.map(msg =>
          msg.id === progressMessageId
            ? {
                ...msg,
                content: `${t('chat.error.prefix')} ${errorMessage}. ${t('chat.error.retry')}`
              }
            : msg
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setError(null);
    setSessionId(null);
    const welcomeMessage: ChatMessage = {
      id: 'welcome_new',
      type: 'bot',
      content: t('chat.cleared'),
      timestamp: new Date(),
      sources: []
    };
    setMessages([welcomeMessage]);
  };

  const renderContent = (text: string, messageType: 'user' | 'bot'): React.ReactNode => {
    const parts = text.split(/(\*\*.*?\*\*)/g);

    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={idx}>{part.slice(2, -2)}</strong>;
      }

      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const subParts = part.split(urlRegex);

      return subParts.map((subPart, subIdx) => {
        if (subPart.match(urlRegex)) {
          return (
            <a
              key={`${idx}-${subIdx}`}
              href={subPart}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:opacity-80 font-medium break-all"
              style={{ color: messageType === 'user' ? '#fff' : '#2563eb' }}
            >
              {subPart}
            </a>
          );
        }
        return <React.Fragment key={`${idx}-${subIdx}`}>{subPart}</React.Fragment>;
      });
    });
  };

  return (
    <div className="card p-6 flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <MessageSquare className="h-6 w-6 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">{t('chat.title')}</h3>
        </div>
        <button
          onClick={clearChat}
          className="text-xs bg-red-600 text-white px-3 py-1.5 rounded-md hover:bg-red-700 transition-colors"
        >
          {t('chat.reset')}
        </button>
      </div>

      <ConversationSelector
        currentSessionId={sessionId}
        onSelectConversation={handleSelectConversation}
      />

      {loadingHistory && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
          <span className="ml-2 text-sm text-gray-600">{t('chat.loadingHistory')}</span>
        </div>
      )}

      <div className="flex-1 overflow-y-auto space-y-3 mb-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex items-start space-x-2 min-w-0 ${
              message.type === 'user' ? 'justify-end' : 'justify-start'
            }`}
          >
            {message.type === 'bot' && (
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <Bot className="h-4 w-4 text-primary-600" />
              </div>
            )}

            <div
              className={`max-w-[85%] min-w-0 px-3 py-2 rounded-lg ${
                message.type === 'user'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-900'
              }`}
              style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
            >
              <p className="text-sm whitespace-pre-wrap" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                {renderContent(message.content, message.type)}
              </p>
              
              {message.sources && message.sources.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <p className="text-xs text-gray-600 mb-1">{t('chat.sources')}:</p>
                  {message.sources.map((source, index) => (
                    <div key={index} className="text-xs text-gray-500 break-words">
                      <span className="font-medium">{source.type}</span>
                      {source.title && `: ${source.title}`}
                    </div>
                  ))}
                </div>
              )}

              <p className="text-xs opacity-75 mt-1 break-words">
                {message.timestamp.toLocaleTimeString()}
              </p>
            </div>

            {message.type === 'user' && (
              <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                <User className="h-4 w-4 text-gray-600" />
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {error && (
        <div className="bg-error-50 border border-error-200 rounded-md p-2 mb-3">
          <p className="text-sm text-error-600">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex space-x-2 items-end">
        <textarea
          ref={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          placeholder={t('chat.placeholder')}
          className="input-field flex-1 text-sm resize-none overflow-y-auto"
          disabled={loading}
          maxLength={1000}
        />
        <button
          type="submit"
          disabled={loading || !inputValue.trim()}
          className="btn-primary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </form>

      <p className="text-xs text-gray-500 mt-2 text-center">{t('chat.note')}</p>
    </div>
  );
};

export default ChatWidget;